import React from "react";
import { SlaAlert } from "./models/sla-alert";

interface SlaAlertOverlayProps {
      alerts: SlaAlert[];
      collapsed: boolean;
      onToggleCollapse: () => void;
}

function formatCountdown(businessMinutes: number): string {
      if (businessMinutes <= 0) return "OVERDUE";
      const totalHrs = Math.floor(businessMinutes / 60);
      const mins = Math.floor(businessMinutes % 60);

      if (totalHrs >= 1) return `${totalHrs} hr${totalHrs > 1 ? 's' : ''}, ${mins} min`;
      return `${mins} min`;
}

function getHighestSeverity(alerts: SlaAlert[]): string {
      if (alerts.some(a => a.status === 'OVERDUE' || a.status === 'EMERGENCY')) return 'emergency';
      if (alerts.some(a => a.status === 'CRITICAL')) return 'critical';
      return 'warning';
}

const SlaAlertOverlay = ({ alerts, collapsed, onToggleCollapse }: SlaAlertOverlayProps) => {
      const [now, setNow] = React.useState(Date.now());

      React.useEffect(() => {
            const interval = setInterval(() => {
                  setNow(Date.now());
            }, 1000);
            return () => clearInterval(interval);
      }, []);

      if (!alerts || alerts.length === 0) return null;

      const severity = getHighestSeverity(alerts);

      // Collapsed: thin bar at bottom with summary
      if (collapsed) {
            const nearest = Math.min(...alerts.map(a => a.businessMinutesRemaining));
            const count = alerts.length;
            const icon = severity === 'emergency' ? '\u{1F6A8}' : '\u26A0\uFE0F';
            const summary = `${icon} ${count} SLA alert${count !== 1 ? 's' : ''} \u2014 nearest due in ${formatCountdown(nearest)}`;

            return (
                  <div className={`sla-marquee-collapsed ${severity}`} onClick={onToggleCollapse}>
                        <span className="sla-marquee-collapsed-text">{summary}</span>
                        <span className="sla-marquee-toggle-icon">{'\u25B4'}</span>
                  </div>
            );
      }

      // Expanded: full marquee band
      const bandClass = `sla-marquee-band ${severity}`;

      const tickerSegments = alerts.map((alert) => {
            const icon = alert.status === 'OVERDUE' || alert.status === 'EMERGENCY' ? '\u{1F6A8}' : '\u26A0\uFE0F';
            const timeStr = formatCountdown(alert.businessMinutesRemaining);
            return `${icon}  T${alert.orderNumber} - ${alert.frameSize}" ${alert.frameName}  \u2014  Due in ${timeStr}`;
      });

      const tickerText = tickerSegments.join('          ');

      return (
            <div className={bandClass}>
                  <button className="sla-marquee-toggle" onClick={onToggleCollapse} title="Collapse">
                        &#9662;
                  </button>
                  <div className="sla-marquee-track">
                        <span className="sla-marquee-content">{tickerText}</span>
                        <span className="sla-marquee-content">{tickerText}</span>
                  </div>
            </div>
      );
};

export default SlaAlertOverlay;
